import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Coins } from 'lucide-react';
import type { PriceTypeToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { usePriceTypesList, usePriceTypeMutations } from '~/hooks/use-price-types';
import { tokenToForm, type PriceTypeFormValues } from '~/lib/schemas/price-type';
import { PriceTypeEditDialog } from './price-type-edit-dialog';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Price types (Είδη Τιμών) CRUD surface — parity with the legacy admin grid (Name/Base/Tax/Visible/Sort). */
export function PriceTypesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = usePriceTypesList();
	const { remove } = usePriceTypeMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<PriceTypeFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<PriceTypeToken | null>(null);

	const columns = useMemo<SystemGridColumn<PriceTypeToken>[]>(
		() => [
			{
				key: 'name',
				header: t('prices.priceTypes.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.nm ?? '',
				exportValue: o => o.nm ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.nm ?? ''}>
						{o.nm || '—'}
					</span>
				)
			},
			{
				key: 'base',
				header: t('prices.priceTypes.columns.base'),
				width: '110px',
				align: 'center',
				sortValue: o => (o.base ? 1 : 0),
				exportValue: o => (o.base ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.base)} />
			},
			{
				key: 'tax',
				header: t('prices.priceTypes.columns.tax'),
				width: '110px',
				align: 'center',
				sortValue: o => (o.tax ? 1 : 0),
				exportValue: o => (o.tax ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.tax)} />
			},
			{
				key: 'visible',
				header: t('prices.priceTypes.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: o => (o.iv ? 1 : 0),
				exportValue: o => (o.iv ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.iv)} />
			},
			{
				key: 'sort',
				header: t('prices.priceTypes.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: o => o.si ?? 0,
				exportValue: o => o.si ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.si}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<PriceTypeToken>
				gridId="price-types"
				title={t('prices.priceTypes.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.nm}
				columns={columns}
				searchPredicate={(o, q) => (o.nm ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<Coins size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('prices.priceTypes.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(tokenToForm(o));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="price-types"
			/>

			<PriceTypeEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('prices.priceTypes.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('prices.priceTypes.deleteBody', { name: deleteRow?.nm || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
